// FILE: src/blueprint/state/PacksProvider.jsx
// GOAL: Provide info about all available packs via the client proxy route.

import { createContext, useEffect, useMemo, useState } from 'react';
import { fetchPacks } from '../api/clientApi.js';

// Module-level request/cache state shared by all provider instances in this bundle.
// This avoids duplicate pack fetches when multiple app instances mount close together.
let _packsPromise = null;
let _packsCache = null;
let _packsCacheAt = 0;

// Cache pack results for 5 minutes.
const PACKS_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Normalize any value to a string.
 *
 * @param {*} v
 * @return {string}
 */
function asString(v) {
  return v == null ? '' : String(v);
}

/**
 * Normalize one pack object into the shape expected by the UI.
 *
 * RULES:
 * - Accept several possible incoming ID keys.
 * - Always expose a stable `id` field.
 * - Fall back to a generated UI-safe ID when no real ID exists.
 *
 * @param {*} p
 * @param {number} idx
 * @return {Object}
 */
function normalizePack(p, idx) {
  const o = p && typeof p === 'object' ? p : {};
  const idRaw = o.id ?? o.pack_id ?? o.packId ?? '';
  const id = asString(idRaw).trim();

  return {
    ...o,
    id: id || `pack_${idx + 1}`,
  };
}

/**
 * Normalize the WordPress API root so downstream requests can build URLs safely.
 *
 * @param {*} wpApiRoot
 * @return {string}
 */
function normalizeWpApiRoot(wpApiRoot) {
  return asString(wpApiRoot).trim().replace(/\/+$/, '');
}

/**
 * Fetch packs with request deduplication and short-lived caching.
 *
 * FLOW:
 * - Validate the API root.
 * - Reuse cached packs when still fresh.
 * - Reuse an in-flight promise when a fetch is already running.
 * - Otherwise fetch, normalize, cache, and return the result.
 *
 * @param {string} wpApiRoot
 * @param {Object} [options]
 * @param {boolean} [options.force=false]
 * @return {Promise<Array>}
 */
async function fetchPacksOnce(wpApiRoot, { force = false } = {}) {
  const apiRoot = normalizeWpApiRoot(wpApiRoot);

  if (!apiRoot) {
    throw new Error('Missing wpApiRoot for PacksProvider.');
  }

  const now = Date.now();

  // Reuse cached non-empty pack results while the TTL is still valid.
  if (!force && Array.isArray(_packsCache) && _packsCache.length > 0) {
    if (PACKS_CACHE_TTL_MS === 0) return _packsCache;
    if (now - _packsCacheAt < PACKS_CACHE_TTL_MS) return _packsCache;
  }

  // Reuse the current in-flight request when one already exists.
  if (!force && _packsPromise) return _packsPromise;

  _packsPromise = (async () => {
    const t1 = performance.now();
    const res = await fetchPacks({ wpApiRoot: apiRoot });
    console.log('fetchPacks ms', Math.round(performance.now() - t1));

    // Accept either an envelope-style response or a raw array.
    const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

    console.dir(list);

    const normalized = list.map(normalizePack);

    console.dir(normalized);

    _packsCache = normalized;
    _packsCacheAt = Date.now();

    return normalized;
  })();

  try {
    return await _packsPromise;
  } finally {
    // Clear the shared in-flight marker once the request settles.
    _packsPromise = null;
  }
}

export const PACKS_ACTIONS = Object.freeze({
  PACKS_LOADING: 'PACKS_LOADING',
  PACKS_SUCCESS: 'PACKS_SUCCESS',
  PACKS_ERROR: 'PACKS_ERROR',
  PACKS_SET: 'PACKS_SET',
  PACKS_REFRESH: 'PACKS_REFRESH',
});

export const PacksContext = createContext({
  statePacks: { packs: [], isLoading: false, error: '' },
  dispatchPacks: () => {},
});

/**
 * Global provider for pack discovery/list state.
 *
 * STATE:
 * - packs
 * - isLoading
 * - error
 *
 * API:
 * - dispatchPacks(action)
 *
 * @param {Object} props
 * @param {*} props.children
 * @param {string} [props.wpApiRoot]
 * @param {string} [props.wpNonce]
 * @return {JSX.Element}
 */
export function PacksProvider({ children, wpApiRoot = '', wpNonce = '' }) {
  const [state, setState] = useState({
    packs: [],
    isLoading: true,
    error: '',
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const packs = await fetchPacksOnce(wpApiRoot);

        if (cancelled) return;

        setState({
          packs,
          isLoading: false,
          error: '',
        });
      } catch (err) {
        if (cancelled) return;

        setState({
          packs: [],
          isLoading: false,
          error: err instanceof Error ? err.message : String(err ?? 'Failed to load packs.'),
        });
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [wpApiRoot, wpNonce]);

  const dispatch = useMemo(() => {
    return async (action) => {
      if (!action || typeof action !== 'object') return;

      switch (action.type) {
        case PACKS_ACTIONS.PACKS_LOADING: {
          const isLoading = Boolean(action?.payload?.isLoading);
          setState((prev) => ({ ...prev, isLoading }));
          return;
        }

        case PACKS_ACTIONS.PACKS_ERROR: {
          const error = String(action?.payload?.error ?? '');
          setState((prev) => ({ ...prev, error, isLoading: false }));
          return;
        }

        case PACKS_ACTIONS.PACKS_SET:
        case PACKS_ACTIONS.PACKS_SUCCESS: {
          const packsIn = Array.isArray(action?.payload?.packs) ? action.payload.packs : [];
          const packs = packsIn.map(normalizePack);
          setState((prev) => ({ ...prev, packs, error: '', isLoading: false }));
          return;
        }

        case PACKS_ACTIONS.PACKS_REFRESH: {
          setState((prev) => ({ ...prev, isLoading: true, error: '' }));

          try {
            const packs = await fetchPacksOnce(wpApiRoot, { force: true });
            setState((prev) => ({ ...prev, packs, isLoading: false, error: '' }));
          } catch (err) {
            setState((prev) => ({
              ...prev,
              packs: [],
              isLoading: false,
              error: err instanceof Error ? err.message : String(err ?? 'Failed to refresh packs.'),
            }));
          }

          return;
        }

        default:
          return;
      }
    };
  }, [wpApiRoot]);

  const value = useMemo(() => {
    return {
      statePacks: state,
      dispatchPacks: dispatch,
    };
  }, [state, dispatch]);

  return <PacksContext.Provider value={value}>{children}</PacksContext.Provider>;
}

export default PacksProvider;
